<script setup>
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const contactNameInput = ref(null);
const isEditingContactName = ref(false);
const isSavingContactName = ref(false);
const contactNameDraft = ref('');
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id) ||
  props.chat.meta.sender ||
  {}
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};

const startEditingContactName = () => {
  contactNameDraft.value = currentContact.value.name || '';
  isEditingContactName.value = true;
  nextTick(() => contactNameInput.value?.focus?.());
};

const cancelContactNameEdit = () => {
  isEditingContactName.value = false;
  contactNameDraft.value = '';
};

const saveContactName = async () => {
  if (!isEditingContactName.value || isSavingContactName.value) return;

  const trimmedName = contactNameDraft.value.trim();
  const contactId = currentContact.value.id;
  if (!contactId) {
    useAlert('当前客户信息未加载完成，请稍后再试');
    return;
  }

  if (!trimmedName) {
    useAlert('客户名称不能为空');
    return;
  }

  if (trimmedName === currentContact.value.name) {
    cancelContactNameEdit();
    return;
  }

  isSavingContactName.value = true;
  try {
    await store.dispatch('contacts/update', {
      id: contactId,
      name: trimmedName,
    });
    const updatedContact =
      store.getters['contacts/getContact'](contactId) || {
        ...currentContact.value,
        name: trimmedName,
      };
    await store.dispatch('updateConversationContact', {
      conversationId: props.chat.id,
      ...updatedContact,
    });
    useAlert('客户备注名已更新');
    cancelContactNameEdit();
  } catch (error) {
    useAlert(error.message || '客户备注名更新失败');
  } finally {
    isSavingContactName.value = false;
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <InlineInput
            v-if="isEditingContactName"
            ref="contactNameInput"
            v-model="contactNameDraft"
            custom-input-class="!text-sm !font-medium !py-0 !h-6"
            class="max-w-[11rem]"
            :disabled="isSavingContactName"
            @enter-press="saveContactName"
            @escape-press="cancelContactNameEdit"
            @blur="saveContactName"
          />
          <button
            v-else
            v-tooltip="'修改客户备注名'"
            type="button"
            class="group/contact-name inline-flex min-w-0 items-center gap-1 rounded-md px-0.5 text-left hover:text-n-brand"
            @click="startEditingContactName"
          >
            <span
              class="text-sm font-medium truncate leading-tight text-n-slate-12 group-hover/contact-name:text-n-brand"
            >
              {{ currentContact.name }}
            </span>
            <span
              class="i-lucide-pencil size-3 text-n-slate-10 opacity-0 transition-opacity group-hover/contact-name:opacity-100"
            />
          </button>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
